import {
  TouchEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import clsx from 'clsx'
import { toJS } from 'mobx'
import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import { prepareCountPassengers } from 'entities/favorite/lib/prepare-count-passengers'
import promoCodeStore from 'entities/promo-code/model/promo-code-store'
import { isDifficultAviaRoute } from 'entities/ticket/lib/is-difficult-avia-route'
import findTicketFormStore from 'entities/ticket/models/find-ticket/find-ticket-store'
import formPassengersStore from 'entities/ticket/models/form-passengers/form-passengers-store'
import listTicketsStore from 'entities/ticket/models/list-tickets/list-tickets-store'
import TarrifPop from 'entities/ticket/ui/tarrif-pop'
import { setSettingForApp } from 'entities/user/module/app-settings'
import userStore from 'entities/user/module/user-store'

import { ShareKeys } from 'shared/constants'
import { parseAviaShareStorage } from 'shared/lib/avia-share-storage'
import { checkUserAuth } from 'shared/lib/checkUserAuth'
import { getRedirectUrl } from 'shared/lib/getRedirectUrl'
import { prepareDetailTicketInfo } from 'shared/lib/ticketRender'
import { TicketItem } from 'shared/models/ticket'
import serialDocumentStore from 'shared/store/serial-document-store'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { FlightDetailsPopup } from 'shared/ui/flight-details-poup'

import { ArrowDirection } from './components/arrow-direction'
import { Header } from './components/header'
import { ScrollInfo } from './components/scroll-info'
import { TicketScrollIndicator } from './components/ticket-scroll-indicator'
import { TicketSlide } from './components/ticket-slide'
import { SelectTicket } from './components/ticket-tariff'
import {
  TicketSwipeDirection,
  getTicketSwipeGesture,
} from './libs/getTicketSwipeGesture'
import { DetailOffBaggage } from './model'
import styles from './styles.module.scss'

const EDGE_THRESHOLD = 24
const TICKET_TRANSITION_MS = 180
const SCROLL_INFO_OPENED_KEY = 'scroll_info_opened'
const IS_TICKET_SWIPE_ENABLED: boolean = false

const getIsReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

type TicketTransitionDirection = TicketSwipeDirection
type TicketTransitionPhase = 'exit' | 'enter'

interface TicketTransition {
  direction: TicketTransitionDirection
  phase: TicketTransitionPhase
}

interface TouchStartPoint {
  x: number
  y: number
  atTop: boolean
  atBottom: boolean
}

type SwipeGesturePhase = 'pulling' | 'loading'

interface SwipeGesture {
  direction: TicketTransitionDirection
  progress: number
  phase: SwipeGesturePhase
}

const DetailTicket = observer(() => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { t } = useTranslation()
  const { openDialog } = useDialogBottomSheet()
  const containerRef = useRef<HTMLDivElement>(null)
  const touchStartRef = useRef<TouchStartPoint | null>(null)
  const transitionTimeoutsRef = useRef<number[]>([])
  const [ticketTransition, setTicketTransition] =
    useState<TicketTransition | null>(null)
  const [swipeGesture, setSwipeGesture] = useState<SwipeGesture | null>(null)

  const storageTicket = userStore.getClientStateParse(ShareKeys.avia)
  const hasSeenScrollInfo = Boolean(
    userStore.getClientStateString(SCROLL_INFO_OPENED_KEY),
  )
  const areUserSettingsLoaded = Boolean(userStore.userSettings)

  const getSearchPath = () => RoutingPaths.avia.search.path()

  const onBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
      return
    }

    navigate(getSearchPath())
  }

  const onSelectTicket = (selectTicket: SelectTicket) => {
    const navigatePath = RoutingPaths.avia.insuranceService.path()
    const redirectUrl = getRedirectUrl(navigatePath)

    checkUserAuth({
      redirectUrl,
      onSuccess: () => {
        formPassengersStore.resetTicketInsurances()

        listTicketsStore.setSelectedTicket(selectTicket)

        void setSettingForApp({ key: ShareKeys.avia, value: '' })

        promoCodeStore.resetPromoInfo('avia')

        promoCodeStore.resetPromoInfo('avia')

        navigate(navigatePath)
      },
      onError: () => {
        void setSettingForApp({
          key: ShareKeys.avia,
          value: JSON.stringify(toJS(selectTicket)),
        })
      },
    })
  }

  const onDetailBaggage = ({ indexes, ticket }: DetailOffBaggage) => {
    const detailTicket = prepareDetailTicketInfo(ticket)

    const [directionNum, segmentNum] = indexes
    const segment =
      detailTicket?.resultsAllListDirections?.[directionNum]?.[segmentNum]

    openDialog({
      child: (
        <FlightDetailsPopup
          flightCode={`${t('flight')} ${segment?.carrier.code}-${segment?.flight_number}`}
          timeFlight={`${segment?.dep?.time || ''}`}
          airplane={segment?.aircraft?.title || ''}
          baggage={segment?.baggage}
          cBaggage={segment?.cbaggage}
        />
      ),
    })
  }

  const onShowTariff = (ticket: TicketItem) => {
    if (!ticket) return
    openDialog({
      child: (
        <TarrifPop
          rulesParams={ticket}
          additionalTariff={ticket?.bookit_fare_data?.fares?.description}
        />
      ),
    })
  }

  const listOfTickets = listTicketsStore.getListTickets()
  const hasTicketList = Boolean(listOfTickets.length)
  const indexParam = searchParams.get('index')
  const parsedIndex = Number(indexParam)
  const indexFromQuery =
    Number.isInteger(parsedIndex) && parsedIndex >= 0 ? parsedIndex : null
  const requestedTicketIndex =
    listTicketsStore.activeTicketIndex ?? indexFromQuery ?? 0
  const ticketIndex = hasTicketList
    ? Math.min(Math.max(requestedTicketIndex, 0), listOfTickets.length - 1)
    : 0

  const fallbackTicket = useMemo(() => {
    const fromStorage = storageTicket
      ? parseAviaShareStorage(storageTicket)
      : null

    if (fromStorage?.ticket) return fromStorage.ticket

    return listTicketsStore?.selectedTicket?.ticket || null
  }, [storageTicket, listTicketsStore?.selectedTicket?.ticket])

  const currentTicket = hasTicketList
    ? listOfTickets[ticketIndex]
    : fallbackTicket

  const syncTicketIndex = useCallback(
    (index: number) => {
      listTicketsStore.setActiveTicketIndex(index)

      const nextSearchParams = new URLSearchParams(searchParams)
      nextSearchParams.set('index', String(index))
      setSearchParams(nextSearchParams, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const totalCountPassengers = findTicketFormStore.getCountOfPassengers()

  const directions = toJS(findTicketFormStore.formFindTicket?.directions)
  const isDifficultRoute = isDifficultAviaRoute(directions)

  const clearTransitionTimeouts = useCallback(() => {
    transitionTimeoutsRef.current.forEach(window.clearTimeout)
    transitionTimeoutsRef.current = []
  }, [])

  const transitionToTicket = useCallback(
    (nextIndex: number) => {
      if (ticketTransition) return
      if (nextIndex < 0) return

      const direction: TicketTransitionDirection =
        nextIndex > ticketIndex ? 'next' : 'prev'

      const scrollNewTicketToTop = () => {
        containerRef.current?.scrollTo({ top: 0 })
      }

      if (getIsReducedMotion()) {
        syncTicketIndex(nextIndex)
        window.requestAnimationFrame(scrollNewTicketToTop)
        return
      }

      clearTransitionTimeouts()
      setTicketTransition({ direction, phase: 'exit' })

      const exitTimeout = window.setTimeout(() => {
        syncTicketIndex(nextIndex)

        window.requestAnimationFrame(() => {
          scrollNewTicketToTop()
          setTicketTransition({ direction, phase: 'enter' })

          const enterTimeout = window.setTimeout(() => {
            setTicketTransition(null)
          }, TICKET_TRANSITION_MS)

          transitionTimeoutsRef.current.push(enterTimeout)
        })
      }, TICKET_TRANSITION_MS)

      transitionTimeoutsRef.current.push(exitTimeout)
    },
    [clearTransitionTimeouts, syncTicketIndex, ticketIndex, ticketTransition],
  )

  const loadAndSwitchToNext = useCallback(async () => {
    if (
      listTicketsStore.loading ||
      listTicketsStore.page > listTicketsStore.maxPage
    ) {
      setSwipeGesture(null)
      return
    }

    const previousCount = listTicketsStore.getListTickets().length

    setSwipeGesture({ direction: 'next', progress: 1, phase: 'loading' })

    await listTicketsStore.onGetTicketRequest({
      form: findTicketFormStore.formFindTicket,
      params: {
        isDifficultRoute,
      },
    })

    const nextCount = listTicketsStore.getListTickets().length

    setSwipeGesture(null)

    if (nextCount > previousCount) {
      transitionToTicket(previousCount)
    }
  }, [isDifficultRoute, transitionToTicket])

  const hasMorePages = listTicketsStore.page <= listTicketsStore.maxPage
  const canSwipeNext = ticketIndex < listOfTickets.length - 1 || hasMorePages
  const canSwipePrev = ticketIndex > 0
  const canBrowseTickets = hasTicketList && (canSwipeNext || canSwipePrev)
  const totalTicketsCount =
    listTicketsStore.totalItems !== null &&
    listTicketsStore.totalItems >= listOfTickets.length
      ? listTicketsStore.totalItems
      : undefined

  const onTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0]
    const container = containerRef.current

    if (
      !touch ||
      !container ||
      !hasTicketList ||
      listTicketsStore.loading ||
      ticketTransition ||
      swipeGesture?.phase === 'loading'
    ) {
      return
    }

    const { scrollTop } = container

    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      atTop: scrollTop <= EDGE_THRESHOLD,
      atBottom:
        scrollTop + container.clientHeight >=
        container.scrollHeight - EDGE_THRESHOLD,
    }

    setSwipeGesture(null)
  }

  const onTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    const startPoint = touchStartRef.current
    const touch = event.touches[0]

    if (
      !startPoint ||
      !touch ||
      listTicketsStore.loading ||
      ticketTransition ||
      swipeGesture?.phase === 'loading'
    ) {
      return
    }

    const gesture = getTicketSwipeGesture({
      deltaX: touch.clientX - startPoint.x,
      deltaY: touch.clientY - startPoint.y,
      atTop: startPoint.atTop,
      atBottom: startPoint.atBottom,
      canSwipeNext,
      canSwipePrev,
    })

    if (!gesture) {
      setSwipeGesture(null)
      return
    }

    setSwipeGesture({
      ...gesture,
      phase: 'pulling',
    })
  }

  const onTouchEnd = async (event: TouchEvent<HTMLDivElement>) => {
    const startPoint = touchStartRef.current
    const touch = event.changedTouches[0]

    touchStartRef.current = null

    if (
      !startPoint ||
      !touch ||
      !hasTicketList ||
      listTicketsStore.loading ||
      ticketTransition ||
      swipeGesture?.phase === 'loading'
    ) {
      return
    }

    const gesture = getTicketSwipeGesture({
      deltaX: touch.clientX - startPoint.x,
      deltaY: touch.clientY - startPoint.y,
      atTop: startPoint.atTop,
      atBottom: startPoint.atBottom,
      canSwipeNext,
      canSwipePrev,
    })

    if (!gesture || gesture.progress < 1) {
      setSwipeGesture(null)
      return
    }

    if (gesture.direction === 'next') {
      if (ticketIndex < listOfTickets.length - 1) {
        setSwipeGesture(null)
        transitionToTicket(ticketIndex + 1)
        return
      }

      await loadAndSwitchToNext()
      return
    }

    setSwipeGesture(null)

    if (gesture.direction === 'prev') {
      transitionToTicket(ticketIndex - 1)
    }
  }

  const onTouchCancel = () => {
    touchStartRef.current = null

    if (swipeGesture?.phase !== 'loading') {
      setSwipeGesture(null)
    }
  }

  const favoritesParams = {
    params: prepareCountPassengers(findTicketFormStore.formFindTicket),
    directions,
  }

  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    if (!serialDocumentStore?.info?.length) {
      void serialDocumentStore.getSerialDocumentInfo()
    }
  }, [])

  useEffect(() => {
    return clearTransitionTimeouts
  }, [clearTransitionTimeouts])

  useEffect(() => {
    if (
      !IS_TICKET_SWIPE_ENABLED ||
      !areUserSettingsLoaded ||
      hasSeenScrollInfo ||
      !canBrowseTickets
    ) {
      return
    }

    setSettingForApp({
      key: SCROLL_INFO_OPENED_KEY,
      value: true,
    }).catch(() => undefined)

    openDialog({
      child: <ScrollInfo />,
      backgroundColor: 'var(--white)',
    })
  }, [areUserSettingsLoaded, canBrowseTickets, hasSeenScrollInfo, openDialog])

  useEffect(() => {
    if (!IS_TICKET_SWIPE_ENABLED || !hasTicketList) return

    if (listTicketsStore.activeTicketIndex !== ticketIndex) {
      listTicketsStore.setActiveTicketIndex(ticketIndex)
    }

    if (indexParam !== String(ticketIndex)) {
      const nextSearchParams = new URLSearchParams(searchParams)
      nextSearchParams.set('index', String(ticketIndex))
      setSearchParams(nextSearchParams, { replace: true })
    }
  }, [hasTicketList, indexParam, searchParams, setSearchParams, ticketIndex])

  useEffect(() => {
    if (currentTicket) return

    navigate(getSearchPath())
  }, [currentTicket])

  return (
    <div
      ref={containerRef}
      className={styles.container}
      onTouchStart={IS_TICKET_SWIPE_ENABLED ? onTouchStart : undefined}
      onTouchMove={IS_TICKET_SWIPE_ENABLED ? onTouchMove : undefined}
      onTouchEnd={IS_TICKET_SWIPE_ENABLED ? onTouchEnd : undefined}
      onTouchCancel={IS_TICKET_SWIPE_ENABLED ? onTouchCancel : undefined}
    >
      {IS_TICKET_SWIPE_ENABLED && swipeGesture && (
        <ArrowDirection
          direction={swipeGesture.direction}
          progress={swipeGesture.progress}
          isLoading={swipeGesture.phase === 'loading'}
        />
      )}

      {IS_TICKET_SWIPE_ENABLED && hasTicketList && (
        <TicketScrollIndicator
          activeIndex={ticketIndex}
          loadedCount={listOfTickets.length}
          totalCount={totalTicketsCount}
          hasMorePages={hasMorePages}
        />
      )}

      <div className={styles.header}>
        <Header
          favoritesParams={favoritesParams}
          ticket={currentTicket || undefined}
          totalCountPassengers={totalCountPassengers}
          onBack={onBack}
        />
      </div>

      {currentTicket && (
        <div
          className={clsx(
            styles.ticketTransition,
            ticketTransition?.direction === 'next' &&
              ticketTransition.phase === 'exit' &&
              styles.nextExit,
            ticketTransition?.direction === 'next' &&
              ticketTransition.phase === 'enter' &&
              styles.nextEnter,
            ticketTransition?.direction === 'prev' &&
              ticketTransition.phase === 'exit' &&
              styles.prevExit,
            ticketTransition?.direction === 'prev' &&
              ticketTransition.phase === 'enter' &&
              styles.prevEnter,
          )}
        >
          <TicketSlide
            key={currentTicket?.tid || ticketIndex}
            ticket={currentTicket}
            index={ticketIndex}
            passengerCount={totalCountPassengers}
            shouldLoadTariff
            onDetailBaggage={onDetailBaggage}
            onShowTariff={onShowTariff}
            onSelectTicket={onSelectTicket}
          />
        </div>
      )}
    </div>
  )
})

export default DetailTicket
